import { useState, useMemo } from 'react'
import { PlusIcon, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { MACHINERY_DIRECTIVE_STUDIO_ID } from '@artem-lex/common-server/complianceStudio'
import {
  useListStandardApplicability,
  useUpdateStandardApplicabilityDecision,
  type StandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { buildProductMachineryDirectiveStandardsDetailSearch } from '@/features/standards-library/standardDetailBackNavigation'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { AddStandardModal } from './AddStandardModal'
import { RerunStandardApplicabilitySheet } from './RerunStandardApplicabilitySheet'
import { StandardsStandardsList } from './StandardsStandardsList'
import { canRejectTypeAStandard } from '../../utils/typeAStandardGuard'

function applicabilityRank(applicability: string): number {
  if (applicability === 'DIRECTLY_APPLICABLE') {
    return 0
  }
  if (applicability === 'CONDITIONALLY_APPLICABLE') {
    return 1
  }
  return 2
}

function certaintyRank(certainty: string): number {
  if (certainty === 'HIGH') {
    return 0
  }
  if (certainty === 'MEDIUM') {
    return 1
  }
  if (certainty === 'LOW') {
    return 2
  }
  return 3
}

function sortStandards(
  standards: StandardApplicability[]
): StandardApplicability[] {
  return [...standards].sort((first, second) => {
    const applicabilityDiff =
      applicabilityRank(first.applicability) -
      applicabilityRank(second.applicability)
    if (applicabilityDiff !== 0) {
      return applicabilityDiff
    }
    const certaintyDiff =
      certaintyRank(first.certainty) - certaintyRank(second.certainty)
    if (certaintyDiff !== 0) {
      return certaintyDiff
    }
    return first.reference.localeCompare(second.reference)
  })
}

export function StandardsPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId ?? ''
  const productId = params.productId ?? ''

  const standardDetailSearch = useMemo(
    () =>
      buildProductMachineryDirectiveStandardsDetailSearch(
        productId,
        complianceId
      ),
    [productId, complianceId]
  )
  const { t } = useTranslation('machineryDirectiveCompliance')

  const [error, setError] = useState<string | null>(null)
  const [isAddStandardModalOpen, setIsAddStandardModalOpen] = useState(false)
  const [isRerunSheetOpen, setIsRerunSheetOpen] = useState(false)

  const {
    data: standards = [],
    isLoading: isLoadingList,
    error: listError,
  } = useListStandardApplicability(complianceId)

  const updateDecisionMutation =
    useUpdateStandardApplicabilityDecision(complianceId)

  const sortedStandards = useMemo(() => sortStandards(standards), [standards])

  const applicableStandards = useMemo(
    () =>
      sortedStandards.filter(
        (standard) =>
          standard.isAccepted === true || standard.isAccepted === null
      ),
    [sortedStandards]
  )
  const rejectedStandards = useMemo(
    () => sortedStandards.filter((standard) => standard.isAccepted === false),
    [sortedStandards]
  )

  const canRejectById = useMemo(() => {
    const rejectById: Record<string, boolean> = {}
    for (const standard of sortedStandards) {
      rejectById[standard.id] = canRejectTypeAStandard(
        sortedStandards,
        standard.id
      )
    }
    return rejectById
  }, [sortedStandards])

  const handleDecision = (standardId: string, isAccepted: boolean) => {
    if (complianceId == null) {
      return
    }
    setError(null)
    updateDecisionMutation.mutate(
      { standardId, isAccepted },
      {
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : t('standardsPage.updateDecisionFailed')
          )
        },
      }
    )
  }

  const displayError =
    error ?? (listError instanceof Error ? listError.message : null)

  if (isLoadingList) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  const hasStandards = sortedStandards.length > 0
  const hasApplicableStandards = applicableStandards.length > 0
  const hasRejectedStandards = rejectedStandards.length > 0
  const hasDisplayError = displayError != null

  return (
    <div>
      <ComplianceSubPageHeader>
        <ComplianceSubPageTitle>{t('nav.standards')}</ComplianceSubPageTitle>
        <div className='flex flex-wrap gap-2'>
          <Button
            variant='outline'
            onClick={() => setIsRerunSheetOpen(true)}
          >
            <RefreshCw className='mr-2 size-4' />
            {t('standardsPage.rerunApplicability')}
          </Button>
          <Button onClick={() => setIsAddStandardModalOpen(true)}>
            <PlusIcon className='mr-2 size-4' />
            {t('standardsPage.addStandard')}
          </Button>
        </div>
      </ComplianceSubPageHeader>

      {displayError != null && (
        <Alert variant='destructive'>
          <AlertTitle>{t('standardsPage.errorTitle')}</AlertTitle>
          <AlertDescription>{displayError}</AlertDescription>
        </Alert>
      )}

      {hasStandards && (
        <Tabs defaultValue='applicable' className='mt-2 w-full'>
          <TabsList>
            <TabsTrigger value='applicable'>
              {t('standardsPage.applicableStandards')} (
              {applicableStandards.length})
            </TabsTrigger>
            <TabsTrigger value='rejected'>
              {t('standardsPage.rejectedStandards')} ({rejectedStandards.length}
              )
            </TabsTrigger>
          </TabsList>
          <TabsContent value='applicable' className='mt-4'>
            {hasApplicableStandards && (
              <StandardsStandardsList
                standards={applicableStandards}
                section='applicable'
                onDecision={handleDecision}
                isUpdating={updateDecisionMutation.isPending}
                canRejectById={canRejectById}
                standardDetailSearch={standardDetailSearch}
              />
            )}
            {!hasApplicableStandards && (
              <p className='py-6 text-center text-muted-foreground'>
                {t('standardsPage.emptyStateMessage')}
              </p>
            )}
          </TabsContent>
          <TabsContent value='rejected' className='mt-4'>
            {hasRejectedStandards && (
              <StandardsStandardsList
                standards={rejectedStandards}
                section='rejected'
                onDecision={handleDecision}
                isUpdating={updateDecisionMutation.isPending}
                canRejectById={canRejectById}
                standardDetailSearch={standardDetailSearch}
              />
            )}
            {!hasRejectedStandards && (
              <p className='py-6 text-center text-muted-foreground'>
                {t('standardsPage.rejectedStandards')} — none yet.
              </p>
            )}
          </TabsContent>
        </Tabs>
      )}
      {!hasStandards && !hasDisplayError && (
        <p className='py-8 text-center text-muted-foreground'>
          {t('standardsPage.emptyStateMessage')}
        </p>
      )}

      {complianceId != null && (
        <>
          <AddStandardModal
            isOpen={isAddStandardModalOpen}
            onClose={() => setIsAddStandardModalOpen(false)}
            complianceId={complianceId}
            implicitComplianceStudioId={MACHINERY_DIRECTIVE_STUDIO_ID}
          />
          <RerunStandardApplicabilitySheet
            complianceId={complianceId}
            isOpen={isRerunSheetOpen}
            onClose={() => setIsRerunSheetOpen(false)}
          />
        </>
      )}
    </div>
  )
}
